import { CaptionTextarea } from '@platejs/caption/react';
import { Image, ImagePlugin, useMediaState } from '@platejs/media/react';
import {
  Resizable,
  ResizableProvider,
  ResizeHandle,
  useResizableValue,
} from '@platejs/resizable';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PLinkPure } from '@porsche-design-system/components-react';
import type { TMediaElement } from 'platejs';
import { useEditorRef, type PlateElementProps } from 'platejs/react';

const DownloadOverlay = styled('div', {
  base: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, .3)',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 'md',
    boxSizing: 'border-box',
    transition: '.3s ease',
    opacity: 0,
  },
});

const ResizeHandleIndicator = styled('div', {
  base: {
    width: '3px',
    height: '60px',
    borderRadius: '3px',
    backgroundColor: 'border.contrastMedium',
    transition: 'background-color 0.2s ease, opacity 0.2s ease',
    opacity: 0,
    pointerEvents: 'none',
  },
});

const Figure = styled('figure', {
  base: {
    position: 'relative',
    textAlign: 'center',
    margin: 0,

    '&:hover [data-part="download-overlay"]': {
      opacity: 1,
    },

    '&:hover [data-part="resize-handle-indicator"]': {
      opacity: 1,
    },
  },
});

const ImageElementInner = ({
  attributes,
  element,
  children,
}: PlateElementProps & {
  element: TMediaElement & { caption?: { text: string }[] };
}) => {
  const editor = useEditorRef();
  const { align = 'center', readOnly } = useMediaState();
  const width = useResizableValue('width');
  const { includeImages, captionPlaceholder, includeImageDownloadLink } =
    editor.getOptions(ImagePlugin) as {
      includeImages?: boolean;
      captionPlaceholder?: string;
      includeImageDownloadLink?: boolean;
    };

  if (!includeImages) {
    return (
      <>{`<<${
        (element.caption as { text: string }[] | undefined)?.[0]?.text ||
        'Image'
      }>>`}</>
    );
  }

  return (
    <div {...attributes} style={{ padding: '10px 0', outline: 'none' }}>
      <Figure contentEditable={false}>
        <Resizable options={{ align, readOnly }}>
          <ResizeHandle
            options={{ direction: 'left' }}
            style={{
              position: 'absolute',
              left: '-14px',
              top: 0,
              bottom: 0,
              width: '12px',
              cursor: 'col-resize',
              zIndex: 2,
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ResizeHandleIndicator data-part="resize-handle-indicator" />
          </ResizeHandle>
          <Image style={{ maxWidth: '100%', display: 'block' }} />
          <ResizeHandle
            options={{ direction: 'right' }}
            style={{
              position: 'absolute',
              right: '-14px',
              top: 0,
              bottom: 0,
              width: '12px',
              cursor: 'col-resize',
              zIndex: 2,
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ResizeHandleIndicator data-part="resize-handle-indicator" />
          </ResizeHandle>

          {includeImageDownloadLink && (
            <DownloadOverlay
              data-part="download-overlay"
              contentEditable={false}
            >
              <PLinkPure
                className="download"
                icon="download"
                hideLabel
                style={{ colorScheme: 'dark' }}
                href={element.url}
                download={element.url.substring(
                  element.url.lastIndexOf('/') + 1,
                )}
              />
            </DownloadOverlay>
          )}
        </Resizable>
        {captionPlaceholder !== undefined && (
          <figcaption
            contentEditable={false}
            style={{ width, textAlign: 'center' }}
          >
            <CaptionTextarea
              readOnly={readOnly}
              placeholder={captionPlaceholder}
              style={{
                border: 'none',
                width: '100%',
                resize: 'none',
                outline: 'none',
                fontFamily: 'unset',
                textAlign: 'center',
                background: 'transparent',
                padding: '4px 0',
              }}
            />
          </figcaption>
        )}
      </Figure>
      {children}
    </div>
  );
};

const Img = (
  props: PlateElementProps & {
    element: TMediaElement & { caption?: { text: string }[] };
  },
) => (
  <ResizableProvider>
    <ImageElementInner {...props} />
  </ResizableProvider>
);

export const imagePlugin = ImagePlugin.configure({
  options: {
    includeImages: true,
    includeImageDownloadLink: false,
    captionPlaceholder: undefined,
  } as Record<string, unknown>,
  render: {
    node: Img,
  },
});
